import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  BottomSheetContainer,
  CarouselHostGestureContext,
  Colors,
  createUseBottomSheetModal,
  Flex,
  IconChevronRight,
  TextButton,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { GoodsLikeRecommend } from './components/GoodsLikeRecommend';
import { View } from 'react-native';
import { bridge, isAndroid, isNotNil } from 'shared/src/utils';
import { PostGoodsActionContext } from './context/PostGoodsActionContext';
import { useLogViewRecommendedModal } from './hooks/useLogViewRecommendedModal';
import { scheduleOnRN } from 'react-native-worklets';
import { useGoodsSno } from '../../hooks/useGoodsSno';
import { useLogGoodsAnalyticsEvent } from '../../hooks/useLogGoodsAnalyticsEvent';
import {
  useAndroidHardwareBackPressHandler,
  useOpenDeeplink,
} from 'shared/src/hooks';
import { AiStudioBanner } from './components/AiStudioBanner';
import { AddCartRecommend } from './components/AddCartRecommend';
import { useIsPostGoodsActionBottomSheetOpen } from '../../atom/isPostGoodsActionBottomSheetOpen.atom';

export type PostGoodsActionBottomSheetParams = {
  image: string;
  mode: 'like' | 'cart';
  title: {
    highlight: string;
    text: string;
  };
  action: {
    text: string;
    deeplink: string;
  };
  folderSno?: number;
};
export const PostGoodsActionBottomSheetContent = ({
  param,
  confirm,
}: {
  param: PostGoodsActionBottomSheetParams;
  confirm: (result: void) => void;
}) => {
  const { height: screenHeight } = useScreenSize();
  const { top, bottom } = useSafeArea();
  const sno = useGoodsSno();
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const { openDeeplink } = useOpenDeeplink();

  const [, setIsBottomSheetOpen] = useIsPostGoodsActionBottomSheetOpen();
  useEffect(() => {
    setIsBottomSheetOpen(true);
    return () => setIsBottomSheetOpen(false);
  }, [setIsBottomSheetOpen]);

  const viewHeight = screenHeight - top - bottom;
  const defaultInitialHeight = isAndroid ? viewHeight * 0.7 : viewHeight * 0.6;
  const heightAnim = useSharedValue(0);
  const startHeight = useSharedValue(defaultInitialHeight);
  const headerHeight = useSharedValue(0);
  const contentHeight = useSharedValue(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [carouselCount, setCarouselCount] = useState(0);
  const [contentHeightState, setContentHeightState] = useState(0);

  const maxHeight = useMemo(() => {
    // Android native(STATE_EXPANDED)는 status bar/nav bar 제외 콘텐츠 가용
    // 영역까지 펼치므로 viewHeight(=screenHeight - top - bottom) + bottom
    // = screenHeight - top 으로 계산해 native 정합.
    if (isAndroid) {
      return viewHeight + bottom;
    }
    return screenHeight * 0.9 + bottom;
  }, [viewHeight, screenHeight, bottom]);

  const initialHeight = useMemo(() => {
    if (carouselCount === 1 && contentHeightState > 0) {
      return Math.min(contentHeightState + bottom, maxHeight);
    }
    return defaultInitialHeight;
  }, [
    carouselCount,
    contentHeightState,
    bottom,
    defaultInitialHeight,
    maxHeight,
  ]);

  useEffect(() => {
    heightAnim.value = withTiming(initialHeight, { duration: 300 });
  }, [heightAnim, initialHeight]);

  const style = useAnimatedStyle(() => ({
    height: heightAnim.value + bottom,
  }));

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetY([-10, 10])
        // 가로로 10px 넘게 움직이면 부모 Pan 을 실패시켜 내부 가로 캐러셀(GHFlatList)에
        // 제스처를 양보한다. activeOffsetY 만으론 가로 드래그 중 부모 Pan 이 미정 상태로
        // 터치를 붙들어(Android onInterceptTouchEvent) 캐러셀이 스크롤되지 않는다.
        .failOffsetX([-5, 5])
        .onStart(() => {
          startHeight.value = heightAnim.value;
        })
        .onUpdate(event => {
          const currentMaxHeight = Math.max(
            contentHeight.value + bottom,
            initialHeight,
          );
          const clampedMax = Math.min(currentMaxHeight, maxHeight);
          const newHeight = startHeight.value - event.translationY;
          heightAnim.value = Math.max(0, Math.min(clampedMax, newHeight));
        })
        .onEnd(event => {
          const currentMaxHeight = Math.max(
            contentHeight.value + bottom,
            initialHeight,
          );
          const clampedMax = Math.min(currentMaxHeight, maxHeight);

          // Android Material BottomSheetBehavior 기준 정합:
          // 속도를 위치에 투영(HIDE_FRICTION=0.1)해 이동량으로 환산한 뒤, 시트 높이의
          // 절반(HIDE_THRESHOLD=0.5)을 넘어야 상태 전환 — 아니면 시작 위치로 스냅백한다.
          // (translationY>0 = 아래로/닫힘 방향, velocityY 도 아래가 +. 하향 플링이면
          //  투영값이 커져 위치가 모자라도 닫히고, 살짝만 움직이면 원위치 복귀한다.)
          const projected = event.translationY + event.velocityY * 0.1;

          if (projected > startHeight.value * 0.5) {
            // 아래로 절반 이상(플링 포함) → 닫기
            heightAnim.value = withTiming(0, { duration: 200 });
            scheduleOnRN(confirm);
            return;
          }

          // 임계값 = startHeight. 시작 높이보다 위로 올라가면(=projected 최종 높이가
          // startHeight 초과, 즉 net 상향 드래그/플링) 무조건 전체(clampedMax)로 펼친다.
          const expandRoom = clampedMax - startHeight.value;
          if (expandRoom > 0 && -projected > 0) {
            heightAnim.value = withTiming(clampedMax, { duration: 200 });
            scheduleOnRN(setIsExpanded, true);
            return;
          }

          // 임계값 미달 → 시작 위치로 스냅백
          heightAnim.value = withTiming(startHeight.value, { duration: 200 });
        }),
    [
      bottom,
      confirm,
      // shared value 는 stable ref 라 deps 로는 ref 만 넣는다. `.value` 를 deps 로
      // 읽으면 render 중 접근이라 reanimated strict warning 이 뜨고, 어차피 worklet
      // (onUpdate/onEnd)이 실행 시점에 최신 `contentHeight.value` 를 읽으므로 불필요.
      contentHeight,
      heightAnim,
      initialHeight,
      maxHeight,
      startHeight,
    ],
  );

  // 시트 드래그 Pan 과 내부 네이티브 스크롤(가로 캐러셀 등)을 simultaneous 로 묶는다.
  // (한쪽 context 선언만으론 Android 에서 Pan 이 터치를 붙들어 가로 스크롤이 안 되던 문제 해소.
  //  GoodsCouponListBottomSheet 와 동일 패턴.)
  // 루트 GestureDetector 의 native gesture 를 named 로 뽑는다. 이게 Android 에서 안쪽 가로
  // 캐러셀 스크롤과 실제로 경쟁하는 상대라, 이걸 CarouselHostGestureContext 로 캐러셀에 넘겨
  // (아래 Provider) 캐러셀 scrollGesture 와 simultaneous 로 묶어야 느린 가로 드래그도 스크롤된다.
  const rootNative = useMemo(
    () => Gesture.Native().shouldCancelWhenOutside(false),
    [],
  );
  const gesture = useMemo(
    () => Gesture.Simultaneous(panGesture, rootNative),
    [panGesture, rootNative],
  );

  useLogViewRecommendedModal(param.mode);

  useAndroidHardwareBackPressHandler(() => {
    confirm();
    return true;
  });

  return (
    <BottomSheetContainer maxHeight={'95%'} hideSafeArea>
      {/* 시트 전체를 Pan 으로 감싸 어디서든 드래그로 펼치기/닫기 가능하게 한다(요구사항).
          안쪽 가로 캐러셀은 CarouselHostGestureContext 로 이 Pan 과
          simultaneousWithExternalGesture 관계를 맺어(ui-kit GoodsCarousel), Android 에서
          Pan 이 미정 상태로 터치를 붙들어 가로 스크롤을 막던 문제를 해소한다. */}
      <GestureDetector gesture={gesture}>
        {/* 캐러셀 host = 실제 경쟁자인 rootNative (기존 panGesture 는 failOffsetX 로 이미
            가로에서 fail 하므로 경쟁자가 아니었음 → 느린 가로 드래그가 안 먹던 원인). */}
        <CarouselHostGestureContext.Provider value={rootNative}>
          <PostGoodsActionContext.Provider value={{ mode: param.mode }}>
            <Animated.View style={style}>
              <View collapsable={false}>
                <Flex.Center
                  padding={{ top: 8, bottom: 4 }}
                  borderBottomWidth={0}
                  borderColor={'white'}
                >
                  <Flex
                    width={40}
                    height={4}
                    backgroundColorValue={Colors.border_primary}
                    borderRadius={2}
                  />
                </Flex.Center>
              </View>

              <Flex flex={1}>
                <Flex
                  onLayout={({ nativeEvent }) => {
                    // sharedValue로 직접 업데이트 (setState 없이)
                    const newHeight = nativeEvent.layout.height;
                    if (newHeight > headerHeight.value) {
                      headerHeight.value = newHeight;
                    }
                  }}
                >
                  <Flex.Row
                    gap={12}
                    padding={{ vertical: 8, left: 16, right: 4 }}
                    borderColor={'border_tertiary'}
                    borderBottomWidth={1}
                  >
                    <Avatar uri={param.image} size={32} />
                    <Flex flex={1} justifyContent={'center'}>
                      <Typography type={'body1'} color={'content_secondary'}>
                        <Typography
                          type={'subtitle1'}
                          color={'content_secondary'}
                        >
                          {param.title.highlight}
                        </Typography>
                        {param.title.text}
                      </Typography>
                    </Flex>

                    <TextButton
                      color="blue"
                      onPress={() => {
                        if (isNotNil(param.folderSno)) {
                          bridge.logAnalyticsEvent({
                            name: 'CLICK_FOLDER',
                            analytics: {
                              SCREEN: 'GOODS_DETAIL',
                              GOODS_SNO: sno,
                              FOLDER_SNO: param.folderSno.toString(),
                              LANDING_FROM: 'TOAST',
                            },
                          });
                        }
                        if (param.mode === 'cart') {
                          logGoodsAnalytics({
                            name: 'CLICK_CART',
                          });
                        }
                        openDeeplink(param.action.deeplink);
                      }}
                      size="small"
                      icon={<IconChevronRight />}
                    >
                      {param.action.text}
                    </TextButton>
                  </Flex.Row>

                  <AiStudioBanner />
                </Flex>
                <Flex flex={1}>
                  <Flex flex={1}>
                    {param.mode === 'like' && (
                      <GoodsLikeRecommend
                        isExpanded={isExpanded}
                        onContentHeightChange={height => {
                          if (height > contentHeight.value) {
                            contentHeight.value = height + bottom;
                          }
                          setContentHeightState(prev =>
                            height > prev ? height : prev,
                          );
                        }}
                        onCarouselCountChange={setCarouselCount}
                      />
                    )}
                    {param.mode === 'cart' && (
                      <AddCartRecommend
                        isExpanded={isExpanded}
                        onContentHeightChange={height => {
                          if (height > contentHeight.value) {
                            contentHeight.value = height + bottom;
                          }
                          setContentHeightState(prev =>
                            height > prev ? height : prev,
                          );
                        }}
                        onCarouselCountChange={setCarouselCount}
                      />
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Animated.View>
          </PostGoodsActionContext.Provider>
        </CarouselHostGestureContext.Provider>
      </GestureDetector>
    </BottomSheetContainer>
  );
};

export const usePostGoodsActionBottomSheet = createUseBottomSheetModal<
  void,
  PostGoodsActionBottomSheetParams
>(
  ({ param, confirm }) => (
    <PostGoodsActionBottomSheetContent param={param} confirm={confirm} />
  ),
  {
    cancelOnBackButtonPress: false,
  },
);
